import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_CUSTOMER_ACCOUNT_INFO,
} from '../../../Constants/Config';

const initialState = {
  loading: false,
  accountInfoData: {},
  error: '',
  requestKey: null,
  activeRequestKey: null,
  activeRequestId: null,
};

const normalizeIdentityPart = (value) =>
  String(value ?? '').trim().toUpperCase();

const isGuestRequest = (value) =>
  ['1', 'TRUE', 'YES'].includes(normalizeIdentityPart(value));

export const createAccountInfoRequestKey = (data) => {
  const userId = normalizeIdentityPart(data?.id);
  if (!userId) return '';

  const merchantId = normalizeIdentityPart(data?.merchant_id);
  const guestMode = isGuestRequest(data?.is_guest) ? 'YES' : 'NO';
  return `user:${userId}|merchant:${merchantId}|guest:${guestMode}`;
};

export const fetchAccoutInfoData = createAsyncThunk(
  'accountInfo/fetchAccoutInfoData',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL_SANDBOX + GET_CUSTOMER_ACCOUNT_INFO,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (
        response?.data?.status === 200 &&
        response.data?.message &&
        typeof response.data.message === 'object'
      ) {
        return response.data;
      }

      return rejectWithValue({
        message: response?.data?.message || 'Unable to load account information',
      });
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Unable to load account information',
      });
    }
  },
);

const accountInfoSlice = createSlice({
  name: 'accountInfo',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchAccoutInfoData.pending, (state, action) => {
      const requestId = action.meta?.requestId;
      if (!requestId) return;

      const requestKey = createAccountInfoRequestKey(action.meta?.arg);
      if (state.requestKey !== requestKey) {
        state.accountInfoData = {};
        state.requestKey = null;
      }
      state.loading = true;
      state.error = '';
      state.activeRequestKey = requestKey;
      state.activeRequestId = requestId;
    });
    builder.addCase(fetchAccoutInfoData.fulfilled, (state, action) => {
      const requestKey = createAccountInfoRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.accountInfoData = action.payload || {};
      state.error = '';
      state.requestKey = requestKey || null;
      state.activeRequestKey = null;
      state.activeRequestId = null;
    });
    builder.addCase(fetchAccoutInfoData.rejected, (state, action) => {
      const requestKey = createAccountInfoRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.accountInfoData = {};
      state.error =
        action.payload?.message ||
        action.error?.message ||
        'Unable to load account information';
      state.requestKey = null;
      state.activeRequestKey = null;
      state.activeRequestId = null;
    });
  },
});

export default accountInfoSlice.reducer;
